import { Assets } from 'pixi.js'
import type { Spritesheet, Texture } from 'pixi.js'

import { log } from '../log'
import type { ResolvedTheme } from './resolve'

/**
 * Art loading — the only part of the theme pipeline that touches Pixi.
 *
 * Two ways in, on purpose:
 *
 *   • `image` — a standalone file per symbol. No build step: drop a PNG in
 *     `art/`, point at it, reload. This is how a skin gets drawn.
 *   • `frame` — a name inside a spritesheet. One texture for the whole
 *     board means one draw call instead of one per distinct symbol. This
 *     is how a skin ships.
 *
 * The same manifest supports both, so moving from loose files to an atlas
 * is a change of two fields, not a rewrite. `frame` wins when both are set.
 *
 * Everything degrades: a missing atlas, a failed request, a frame that is
 * not in the sheet — each falls back to the coloured tile and logs which
 * asset was at fault. Half-themed is a normal state while art is being
 * drawn, not an error.
 */

/**
 * Decode images on the main thread instead of in a worker.
 *
 * Pixi's default path builds that worker from a `blob:` URL, and the
 * production Content-Security-Policy (packages/client/server.mjs) has no
 * `worker-src`, so it falls back to `script-src 'self'` and the browser
 * blocks it. The load then fails and the board never gets its textures —
 * which is what happened to Bird Rush, the one theme that ships an atlas.
 *
 * The alternative was `worker-src 'self' blob:`, i.e. letting a page that
 * moves money run code from a blob URL. The cost here is a little decoding
 * on the main thread, during loading, once.
 *
 * Module scope on purpose: it has to be set before the first Assets.load(),
 * and every load in the client goes through this file.
 */
Assets.setPreferences({ preferWorkers: false })

/** Keys under which non-symbol textures are stored. */
export const BACKGROUND_KEY = '@background'
export const FRAME_KEY = '@frame'
export const BOMB_KEY = '@bomb'

/** Texture key for a banner's stretchable plaque. */
export const bannerKey = (kind: string): string => `@banner:${kind}`

export async function loadThemeAssets(theme: ResolvedTheme): Promise<void> {
  if (theme.textures.size > 0) return
  await Promise.all([loadDisplayFont(theme), loadAtlas(theme), loadLooseImages(theme)])
}

/**
 * Wait for the display face before anything measures a string in it.
 *
 * Canvas text is measured with whatever face is installed at the moment the
 * Text is built. Ask before the webfont arrives and the browser answers for
 * the fallback — the banner plaque was then cut to a width the real letters
 * overflowed, and they ran out over its end caps. The renderer draws no text
 * until its assets are in, so waiting here costs nothing.
 */
async function loadDisplayFont(theme: ResolvedTheme): Promise<void> {
  if (typeof document === 'undefined' || !document.fonts) return
  // First name in the stack: the rest are the fallbacks we are avoiding.
  const family = theme.font.family.split(',')[0]?.trim()
  if (!family) return
  try {
    await document.fonts.load(`${theme.font.weight} 40px "${family}"`)
  } catch (err) {
    log.warn('theme.font-wait-failed', 'text is measured with a fallback face', {
      family,
      reason: err instanceof Error ? err.message : String(err),
    })
  }
}

/** Spritesheet path: frames named by the theme's `frame` fields. */
async function loadAtlas(theme: ResolvedTheme): Promise<void> {
  if (!theme.atlas) return

  let sheet: Spritesheet
  try {
    sheet = await Assets.load<Spritesheet>(theme.atlas.url)
  } catch (err) {
    log.error('theme.atlas-failed', err, 'the atlas did not load', { url: theme.atlas.url })
    return
  }

  const frames = sheet?.textures as Record<string, Texture> | undefined
  if (!frames) {
    log.error('theme.atlas-not-a-sheet', undefined, 'the atlas is not a spritesheet', {
      url: theme.atlas.url,
    })
    return
  }

  const missing: string[] = []
  for (const [code, style] of Object.entries(theme.symbols)) {
    if (!style.frame) continue
    const texture = frames[style.frame]
    if (texture) theme.textures.set(code, texture)
    else missing.push(`${code}→${style.frame}`)
  }
  if (theme.bomb.frame) {
    const texture = frames[theme.bomb.frame]
    if (texture) theme.textures.set(BOMB_KEY, texture)
    else missing.push(`bomb→${theme.bomb.frame}`)
  }
  if (theme.atlas.backgroundFrame) {
    const texture = frames[theme.atlas.backgroundFrame]
    if (texture) theme.textures.set(BACKGROUND_KEY, texture)
    else missing.push(`board→${theme.atlas.backgroundFrame}`)
  }

  // Named individually: "3 frames missing" sends the designer hunting.
  if (missing.length) {
    log.warn('theme.atlas-frames-missing', 'symbols fall back to drawn tiles', { missing })
  }
}

/**
 * Standalone files: symbols, board backdrop, frame overlay.
 *
 * Loaded in parallel and independently — one broken file costs its own
 * symbol, not the whole board.
 */
async function loadLooseImages(theme: ResolvedTheme): Promise<void> {
  const wanted: [key: string, url: string][] = []

  for (const [code, style] of Object.entries(theme.symbols)) {
    // An atlas frame already covers this symbol; do not fetch it twice.
    if (style.image && !theme.textures.has(code)) wanted.push([code, style.image])
    // Flipbook frames. Assets.load caches by URL, so a frame that reuses
    // the base image costs one request, not two.
    // The symbol's own backdrop. Independent of the character layer: a
    // symbol may have a backdrop and no art yet, or the other way round.
    if (style.backgroundImage) wanted.push([bgKey(code), style.backgroundImage])
    if (style.animation) {
      style.animation.images.forEach((url, i) => wanted.push([animKey(code, i), url]))
    }
  }
  if (theme.bomb.image && !theme.textures.has(BOMB_KEY)) {
    wanted.push([BOMB_KEY, theme.bomb.image])
  }
  if (theme.bomb.backgroundImage) wanted.push([bgKey(BOMB_KEY), theme.bomb.backgroundImage])
  if (theme.board.backgroundImage && !theme.textures.has(BACKGROUND_KEY)) {
    wanted.push([BACKGROUND_KEY, theme.board.backgroundImage])
  }
  if (theme.board.frameImage) wanted.push([FRAME_KEY, theme.board.frameImage])
  for (const [kind, style] of Object.entries(theme.banners)) {
    if (style.image) wanted.push([bannerKey(kind), style.image])
  }

  await Promise.all(
    wanted.map(async ([key, url]) => {
      try {
        theme.textures.set(key, await Assets.load<Texture>(url))
      } catch (err) {
        log.warn('theme.image-failed', 'a drawn tile is used instead', {
          url,
          key,
          reason: err instanceof Error ? err.message : String(err),
        })
      }
    }),
  )
}

/** Texture for a symbol code or a reserved key, or null. */
export function textureFor(theme: ResolvedTheme, code: string): Texture | null {
  return (theme.textures.get(code) as Texture | undefined) ?? null
}

/**
 * Backdrop texture of a symbol code (or `BOMB_KEY`), or null.
 *
 * Null covers both "the theme declares none" and "the file failed to
 * load" — the renderer treats them the same way and falls back to `fill`.
 */
export function backgroundTextureFor(theme: ResolvedTheme, code: string): Texture | null {
  return (theme.textures.get(bgKey(code)) as Texture | undefined) ?? null
}

/** Storage key of a symbol's backdrop layer. */
function bgKey(code: string): string {
  return `${code}@bg`
}

/** Storage key of a flipbook frame. */
function animKey(code: string, frame: number): string {
  return `${code}@anim${frame}`
}

/**
 * Flipbook for a symbol: every frame loaded, or null.
 *
 * All-or-nothing on purpose — an animation with a missing frame would
 * stutter in a way that reads as a rendering bug, so it degrades to the
 * static image instead.
 */
export function animationFor(
  theme: ResolvedTheme,
  code: string,
): { textures: Texture[]; fps: number } | null {
  const anim = theme.symbols[code]?.animation
  if (!anim) return null
  const textures: Texture[] = []
  for (let i = 0; i < anim.images.length; i++) {
    const texture = theme.textures.get(animKey(code, i)) as Texture | undefined
    if (!texture) return null
    textures.push(texture)
  }
  return { textures, fps: anim.fps }
}
